import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { DangerouslySetInnerHTML, UnavailableInLens } from '@infl-ts/ui-advocate-components';
import { useToggle } from '@infl-ts/util-hooks';
import { mergeSxProps } from '@infl-ts/util-merge-sx-props';
import { Box, Button, Card, Typography } from '@mui/material';
import * as A from 'fp-ts/Array';
import { snakeCase } from 'lodash';
import { memo, useCallback, useEffect, useRef } from 'react';
import { Translation, useTranslation } from 'react-i18next';
import AdvocateSection from './advocate-section';
import { Answer } from './answer';
import CommentsSection from './comments-section';
import { COMMENTS_TO_FETCH, ToggleButton } from './comments-section/comments-list';
import HideResponseButton from './hide-response-button';
import Likes from './likes';
import { useComments } from './queries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var Responses = function Responses(_ref) {
  var responses = _ref.responses,
      hiddenFromMembers = _ref.hiddenFromMembers;
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      opacity: hiddenFromMembers ? 0.4 : 1
    },
    "data-element": "Box",
    "data-component": "Responses",
    "data-source-file": "public-response-item.tsx",
    children: responses.map(function (_ref2, i) {
      var question = _ref2.question,
          answer = _ref2.answer,
          answerType = _ref2.answerType;
      return /*#__PURE__*/_jsxs(Box, {
        children: [/*#__PURE__*/_jsx(Translation, {
          children: function children(t) {
            return /*#__PURE__*/_jsx(Typography, {
              component: "span",
              variant: "body2",
              sx: {
                my: 1,
                color: function color(t) {
                  return t.palette.text.secondary;
                },
                fontWeight: function fontWeight(t) {
                  return t.typography.fontWeightBold;
                }
              },
              children: /*#__PURE__*/_jsx(DangerouslySetInnerHTML, {
                body: t(question.text)
              })
            });
          }
        }), /*#__PURE__*/_jsx(Answer, {
          answer: answer,
          answerType: answerType
        })]
      }, "".concat(snakeCase(question.text), "-").concat(i));
    })
  });
};

var ReplyButton = function ReplyButton(_ref3) {
  var onClick = _ref3.onClick;
  return /*#__PURE__*/_jsx(UnavailableInLens, {
    tooltip: true,
    "data-element": "UnavailableInLens",
    "data-component": "ReplyButton",
    "data-source-file": "public-response-item.tsx",
    children: /*#__PURE__*/_jsx(Button, {
      sx: {
        color: function color(t) {
          return t.palette.text.secondary;
        },
        fontWeight: function fontWeight(t) {
          return t.typography.fontWeightBold;
        },
        minWidth: 'auto',
        px: 1
      },
      size: "small",
      onClick: onClick,
      "data-element": "Button",
      "data-component": "ReplyButton",
      "data-source-file": "public-response-item.tsx",
      children: /*#__PURE__*/_jsx(Translation, {
        "data-element": "Translation",
        "data-component": "ReplyButton",
        "data-source-file": "public-response-item.tsx",
        children: function children(t) {
          return t('Reply');
        }
      })
    })
  });
};

export var ResponseCard = function ResponseCard(_ref4) {
  var sx = _ref4.sx,
      children = _ref4.children;
  return /*#__PURE__*/_jsx(Card, {
    sx: mergeSxProps({
      borderRadius: {
        xs: 0,
        md: 3
      },
      width: 1,
      mx: {
        xs: 0,
        md: 1
      },
      mt: 1,
      pb: 2
    }, sx),
    raised: true,
    elevation: 4,
    "data-element": "Card",
    "data-component": "ResponseCard",
    "data-source-file": "public-response-item.tsx",
    children: children
  });
};
export var useFetchMoreComments = function useFetchMoreComments(_ref5) {
  var publicResponseId = _ref5.publicResponseId;

  var _useComments = useComments({
    variables: {
      responseId: publicResponseId,
      commentsCursor: '0',
      first: COMMENTS_TO_FETCH
    },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first'
  }),
      comments = _useComments.result,
      fetchMore = _useComments.fetchMore;

  var handleFetchMoreComments = useCallback(function (commentsCursor) {
    return fetchMore({
      variables: {
        first: COMMENTS_TO_FETCH,
        commentsCursor: commentsCursor
      }
    });
  }, [fetchMore]);
  return {
    comments: comments,
    handleFetchMoreComments: handleFetchMoreComments
  };
};

var PublicResponseItem = function PublicResponseItem(_ref6) {
  var _comments$totalCount;

  var _ref6$publicResponse = _ref6.publicResponse,
      __typename = _ref6$publicResponse.__typename,
      id = _ref6$publicResponse.id,
      member = _ref6$publicResponse.member,
      createdAt = _ref6$publicResponse.createdAt,
      responses = _ref6$publicResponse.responses,
      comments = _ref6$publicResponse.comments,
      hiddenFromMembers = _ref6$publicResponse.hidden,
      profile = _ref6.profile,
      _ref6$isFocused = _ref6.isFocused,
      isFocused = _ref6$isFocused === void 0 ? false : _ref6$isFocused,
      _ref6$hasFocusedComme = _ref6.hasFocusedComment,
      hasFocusedComment = _ref6$hasFocusedComme === void 0 ? false : _ref6$hasFocusedComme,
      useScrollToFocused = _ref6.useScrollToFocused;
  var replyInputRef = useRef(null);

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      commentsShown = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      showComments = _useToggle2$.on,
      hideComments = _useToggle2$.off;

  var handleComments = useFetchMoreComments({
    publicResponseId: id
  });

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var onReplyClick = useCallback(function () {
    !commentsShown && showComments();
    setTimeout(function () {
      var _replyInputRef$curren;

      return (_replyInputRef$curren = replyInputRef.current) === null || _replyInputRef$curren === void 0 ? void 0 : _replyInputRef$curren.focus();
    }, 100);
  }, [showComments, commentsShown, replyInputRef]);
  useEffect(function () {
    if (hasFocusedComment && !commentsShown) showComments();
  }, [hasFocusedComment, commentsShown, showComments]);
  return /*#__PURE__*/_jsxs(ResponseCard, {
    sx: {
      background: function background(t) {
        return isFocused ? t.palette.primary.background : undefined;
      },
      transition: !isFocused ? 'background 0.8s ease' : undefined
    },
    "data-element": "ResponseCard",
    "data-component": "PublicResponseItem",
    "data-source-file": "public-response-item.tsx",
    children: [/*#__PURE__*/_jsx(AdvocateSection, {
      sx: {
        p: 3,
        pb: hiddenFromMembers ? 0 : 1,
        overflowWrap: 'anywhere'
      },
      avatarSize: 48,
      member: member,
      timestamp: createdAt,
      hiddenFromMembers: hiddenFromMembers,
      "data-element": "AdvocateSection",
      "data-component": "PublicResponseItem",
      "data-source-file": "public-response-item.tsx",
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [responses && A.isNonEmpty(responses) ? /*#__PURE__*/_jsx(Responses, {
          responses: responses,
          hiddenFromMembers: hiddenFromMembers
        }) : null, /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: 'flex',
            ml: -1,
            gap: 1
          },
          "data-element": "Box",
          "data-source-file": "public-response-item.tsx",
          children: [/*#__PURE__*/_jsx(Likes, {
            likeTargetId: id,
            hideLikeButton: hiddenFromMembers,
            "data-element": "Likes",
            "data-source-file": "public-response-item.tsx"
          }), !hiddenFromMembers && /*#__PURE__*/_jsx(ReplyButton, {
            onClick: onReplyClick
          }), /*#__PURE__*/_jsx(HideResponseButton, {
            profile: profile,
            hiddenFromMembers: hiddenFromMembers,
            id: id,
            "data-element": "HideResponseButton",
            "data-source-file": "public-response-item.tsx"
          })]
        })]
      })
    }), commentsShown ? /*#__PURE__*/_jsx(CommentsSection, {
      publicResponseId: id,
      handleComments: handleComments,
      hiddenFromMembers: hiddenFromMembers,
      totalCount: (_comments$totalCount = comments === null || comments === void 0 ? void 0 : comments.totalCount) !== null && _comments$totalCount !== void 0 ? _comments$totalCount : 0,
      profile: profile,
      handleHideComments: hideComments,
      replyInputRef: replyInputRef,
      useScrollToFocused: useScrollToFocused
    }) : comments !== null && comments !== void 0 && comments.totalCount ? /*#__PURE__*/_jsx(ToggleButton, {
      onClick: showComments,
      label: t("showCommentsText", {
        count: comments.totalCount
      })
    }) : null]
  });
};

export default /*#__PURE__*/memo(PublicResponseItem);